/**
 * useScout — owns the /scout flow (info-first facet drill-down, REVISED).
 *
 *   entry → pick league → SCAN (target league only) → per-member lazy facets:
 *     • otherLeagues  (their other NFL leagues, raw)
 *     • trades/waivers (raw transaction logs, shared pull)
 *     • fingerprint    (the only computed view)
 *
 * Hooks-own-their-domain: all orchestration here; ScoutPage is a thin shell.
 * Heavy work is lazy + per-member — scan touches ONLY the target league.
 * Reuses src/utils/scout/* (incl. scoutMember composition); never reimplements
 * scoring.
 */

import { useState, useRef, useCallback } from "react";
import posthog from "posthog-js";
import { scoutFetch, SCOUT_BASE_URL } from "../utils/scout/scoutFetch.js";
import { SCOUT_SEASONS } from "../utils/scout/scoutConfig.js";
import { toFormatProfile } from "../utils/scout/scoutSimilarity.js";
import { getSleeperPlayersMap } from "../utils/sleeperImport.js";
import { fetchValueMap } from "../utils/scout/scoutValues.js";
import {
  fetchMemberOtherLeagues,
  buildMemberFoundation,
  fetchMemberTransactions,
  buildOwnerDeepDive,
} from "../utils/scout/scoutMember.js";

const isLeagueId = (s) => /^\d{6,}$/.test(s.trim());
const capture = (evt, props) => { try { posthog.capture(evt, props); } catch {} };

export function useScout() {
  const [phase, setPhase] = useState("entry"); // entry | picking | scanning | scan
  const [entryValue, setEntryValue] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [entrantUserId, setEntrantUserId] = useState(null);
  const [userLeagues, setUserLeagues] = useState(null);

  const [target, setTarget] = useState(null);
  const [members, setMembers] = useState([]);
  const [maps, setMaps] = useState({ playersMap: null, valueMap: null }); // for raw log rendering
  const [progress, setProgress] = useState({ message: "", current: 0, total: null });

  // Per-member facet view state: { [uid]: { open, otherLeagues, txn, fingerprint, affinityMeta, progress } }
  const [facets, setFacets] = useState({});
  const setFacet = useCallback((uid, patch) => {
    setFacets((f) => ({ ...f, [uid]: { ...(f[uid] || {}), ...patch } }));
  }, []);
  const facetProg = useCallback(
    (uid) => (message, current, total) => setFacet(uid, { progress: { message, current, total } }),
    [setFacet]
  );

  // Refs for dedup / reading latest loaded data inside async (avoids stale state).
  const mapsRef = useRef({ playersMap: null, valueMap: null });
  const foundationRef = useRef({}); // uid -> foundation
  const otherLeaguesRef = useRef({}); // uid -> leagues[]
  const txnRef = useRef({}); // uid -> txLeagues[]
  const inflight = useRef({}); // uid -> { foundation, otherLeagues, txn, fingerprint }

  const ensureMaps = useCallback(async (tgt) => {
    if (!mapsRef.current.playersMap) mapsRef.current.playersMap = await getSleeperPlayersMap();
    if (!mapsRef.current.valueMap) mapsRef.current.valueMap = (await fetchValueMap(tgt.profile)).valueMap;
    setMaps({ ...mapsRef.current });
    return mapsRef.current;
  }, []);

  // Players DB only (for resolving roster-here names on the scan card) — no
  // cross-league pulls, no value fetch. Sleeper's static players asset, cached.
  const namesInflight = useRef(null);
  const ensureNames = useCallback(async () => {
    if (mapsRef.current.playersMap) return mapsRef.current.playersMap;
    if (namesInflight.current) return namesInflight.current;
    namesInflight.current = (async () => {
      mapsRef.current.playersMap = await getSleeperPlayersMap();
      setMaps({ ...mapsRef.current });
      return mapsRef.current.playersMap;
    })();
    return namesInflight.current;
  }, []);

  // ── Scan: target league only ────────────────────────────────────────────
  const scanLeague = useCallback(async (leagueId, entrant) => {
    setPhase("scanning"); setBusy(true); setError(null); setFacets({});
    foundationRef.current = {}; otherLeaguesRef.current = {}; txnRef.current = {}; inflight.current = {};
    setProgress({ message: "Reading league…", current: 0, total: null });
    const t0 = performance.now();
    try {
      const lid = String(leagueId).trim();
      const league = await scoutFetch(`${SCOUT_BASE_URL}/league/${lid}`, { cacheId: lid, season: "meta", dataType: "scout_league" });
      if (!league || !league.league_id) throw new Error("League not found");
      const season = league.season || SCOUT_SEASONS[0];
      const [rosters, users] = await Promise.all([
        scoutFetch(`${SCOUT_BASE_URL}/league/${lid}/rosters`, { cacheId: lid, season, dataType: "scout_rosters" }),
        scoutFetch(`${SCOUT_BASE_URL}/league/${lid}/users`, { cacheId: lid, season, dataType: "scout_users" }),
      ]);
      const userById = new Map((Array.isArray(users) ? users : []).map((u) => [u.user_id, u]));
      const mem = (Array.isArray(rosters) ? rosters : [])
        .filter((r) => r.owner_id && r.owner_id !== entrant)
        .map((r) => {
          const u = userById.get(r.owner_id) || {};
          const st = r.settings || {};
          return {
            user_id: r.owner_id,
            display_name: u.display_name || r.owner_id,
            team_name: (u.metadata || {}).team_name || u.display_name || null,
            roster_id: r.roster_id,
            standing: { wins: st.wins || 0, losses: st.losses || 0, ties: st.ties || 0, fpts: (st.fpts || 0) + (st.fpts_decimal || 0) / 100 },
            players: r.players || [],
          };
        });
      const tgt = { league_id: league.league_id, name: league.name || null, season, total_rosters: league.total_rosters ?? mem.length, profile: toFormatProfile(league) };
      setTarget(tgt);
      setMembers(mem);
      setPhase("scan");
      capture("scout_scan", { members: mem.length, elapsedSeconds: Math.round((performance.now() - t0) / 1000) });
    } catch (e) {
      setError(e?.message || "Scan failed");
      setPhase(userLeagues ? "picking" : "entry");
    } finally {
      setBusy(false);
    }
  }, [userLeagues]);

  const submitEntry = useCallback(async () => {
    const v = entryValue.trim();
    if (!v) return;
    setError(null);
    if (isLeagueId(v)) { capture("scout_entry", { mode: "league_id" }); scanLeague(v, null); return; }
    setBusy(true);
    try {
      const user = await scoutFetch(`${SCOUT_BASE_URL}/user/${encodeURIComponent(v)}`, {});
      if (!user || !user.user_id) { setError("Sleeper user not found"); setBusy(false); return; }
      setEntrantUserId(user.user_id);
      const byId = new Map();
      for (const season of SCOUT_SEASONS) {
        const ls = await scoutFetch(`${SCOUT_BASE_URL}/user/${user.user_id}/leagues/nfl/${season}`, { cacheId: user.user_id, season, dataType: "scout_user_leagues" });
        for (const l of Array.isArray(ls) ? ls : []) if (l?.league_id && !byId.has(l.league_id)) byId.set(l.league_id, l);
      }
      const leagues = [...byId.values()];
      if (!leagues.length) { setError("No NFL leagues found for that user"); setBusy(false); return; }
      setUserLeagues(leagues); setPhase("picking");
      capture("scout_entry", { mode: "username", leagueCount: leagues.length });
    } catch (e) {
      setError(e?.message || "Lookup failed");
    } finally { setBusy(false); }
  }, [entryValue, scanLeague]);

  const pickLeague = useCallback((leagueId) => scanLeague(leagueId, entrantUserId), [scanLeague, entrantUserId]);

  // ── Facet loaders (lazy, per-member) ─────────────────────────────────────
  const ensureOtherLeagues = useCallback(async (member) => {
    const uid = member.user_id;
    if (otherLeaguesRef.current[uid]) return otherLeaguesRef.current[uid];
    if (inflight.current[uid]?.otherLeagues) return inflight.current[uid].otherLeagues;
    setFacet(uid, { otherLeagues: { loading: true } });
    const p = (async () => {
      const leagues = await fetchMemberOtherLeagues(uid, { onProgress: facetProg(uid) });
      otherLeaguesRef.current[uid] = leagues;
      setFacet(uid, { otherLeagues: { loading: false, leagues } });
      return leagues;
    })().catch((e) => { setFacet(uid, { otherLeagues: { loading: false, error: e?.message || "Failed" } }); });
    inflight.current[uid] = { ...(inflight.current[uid] || {}), otherLeagues: p };
    return p;
  }, [setFacet, facetProg]);

  const ensureFoundation = useCallback(async (member) => {
    const uid = member.user_id;
    if (foundationRef.current[uid]) return foundationRef.current[uid];
    if (inflight.current[uid]?.foundation) return inflight.current[uid].foundation;
    const p = (async () => {
      const m = await ensureMaps(target);
      const foundation = await buildMemberFoundation(member, target, {
        playersMap: m.playersMap, valueMap: m.valueMap,
        otherLeagues: otherLeaguesRef.current[uid] || null, onProgress: facetProg(uid),
      });
      foundationRef.current[uid] = foundation;
      setFacet(uid, { affinityMeta: foundation.affinityMeta });
      return foundation;
    })();
    inflight.current[uid] = { ...(inflight.current[uid] || {}), foundation: p };
    return p;
  }, [ensureMaps, target, setFacet, facetProg]);

  const ensureTransactions = useCallback(async (member) => {
    const uid = member.user_id;
    if (txnRef.current[uid]) { setFacet(uid, { txn: { loading: false, leagues: txnRef.current[uid] } }); return txnRef.current[uid]; }
    if (inflight.current[uid]?.txn) return inflight.current[uid].txn;
    setFacet(uid, { txn: { loading: true } });
    const p = (async () => {
      const foundation = await ensureFoundation(member);
      const txLeagues = await fetchMemberTransactions(foundation.affinity, { onProgress: facetProg(uid) });
      txnRef.current[uid] = txLeagues;
      setFacet(uid, { txn: { loading: false, leagues: txLeagues } });
      return txLeagues;
    })().catch((e) => { setFacet(uid, { txn: { loading: false, error: e?.message || "Failed" } }); });
    inflight.current[uid] = { ...(inflight.current[uid] || {}), txn: p };
    return p;
  }, [ensureFoundation, setFacet, facetProg]);

  const ensureFingerprint = useCallback(async (member) => {
    const uid = member.user_id;
    if (facets[uid]?.fingerprint?.dd) return;
    if (inflight.current[uid]?.fingerprint) return inflight.current[uid].fingerprint;
    setFacet(uid, { fingerprint: { loading: true } });
    const t0 = performance.now();
    const p = (async () => {
      const foundation = await ensureFoundation(member);
      const dd = await buildOwnerDeepDive(
        { user_id: uid, affinity: foundation.affinity, believers: foundation.believers, affinityMeta: foundation.affinityMeta },
        { profile: target.profile, currentSeason: target.season, playersMap: mapsRef.current.playersMap, valueMap: mapsRef.current.valueMap, onProgress: facetProg(uid) }
      );
      setFacet(uid, { fingerprint: { loading: false, dd } });
      capture("scout_fingerprint", { archetype: dd.archetype?.archetype || null, txLeagues: dd.txLeagueCount, reachability: dd.reachability?.band, elapsedSeconds: Math.round((performance.now() - t0) / 1000) });
    })().catch((e) => { setFacet(uid, { fingerprint: { loading: false, error: e?.message || "Failed" } }); });
    inflight.current[uid] = { ...(inflight.current[uid] || {}), fingerprint: p };
    return p;
  }, [ensureFoundation, target, setFacet, facets, facetProg]);

  const openFacet = useCallback((member, facet) => {
    const uid = member.user_id;
    const cur = facets[uid] || {};
    if (cur.open === facet) { setFacet(uid, { open: null }); return; }
    setFacet(uid, { open: facet });
    capture("scout_facet_open", { facet });
    if (facet === "otherLeagues") ensureOtherLeagues(member);
    else if (facet === "trades" || facet === "waivers") ensureTransactions(member);
    else if (facet === "fingerprint") ensureFingerprint(member);
  }, [facets, setFacet, ensureOtherLeagues, ensureTransactions, ensureFingerprint]);

  const back = useCallback(() => {
    setPhase("entry"); setMembers([]); setTarget(null); setUserLeagues(null);
    setFacets({}); setError(null);
    foundationRef.current = {}; otherLeaguesRef.current = {}; txnRef.current = {}; inflight.current = {};
  }, []);

  return {
    phase, entryValue, setEntryValue, error, busy,
    userLeagues, target, members, progress, facets, maps,
    submitEntry, pickLeague, openFacet, ensureNames, back,
  };
}
